import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { SideMenuPageContainer } from "../../components/side_menu_page_container/SideMenuPageContainer";
import Grid2 from "@mui/material/Unstable_Grid2/Grid2";
import {
  logoChanged,
  invoiceNumberChanged,
  dateChanged,
  currencyChanged,
  currencyDisplayTypeChanged,
  getCurrencyDisplay,
  fromNameChanged,
  fromAddressChanged,
  fromCityChanged,
  fromStateChanged,
  fromZipChanged,
  fromCountryChanged,
  fromEmailChanged,
  fromPhoneChanged,
  billToNameChanged,
  billToAddressChanged,
  billToCityChanged,
  billToStateChanged,
  billToZipChanged,
  billToCountryChanged,
  billToEmailChanged,
  billToPhoneChanged,
  shipToNameChanged,
  shipToAddressChanged,
  shipToCityChanged,
  shipToStateChanged,
  shipToZipChanged,
  shipToCountryChanged,
  paymentTermsChanged,
  dueDateChanged,
  poNumberChanged,
  itemAdded,
  itemUpdated,
  itemRemoved,
  taxPercentageChanged,
  discountPercentageChanged,
  shippingChanged,
  amountPaidChanged,
  notesChanged,
  termsChanged,
  clearError,
  markAsDownloaded,
  resetGenerator,
  generateInvoicePdfAsync,
  validateLogoFile,
  generateUniqueId,
  selectState,
  InvoiceItem,
} from "./InvoiceGeneratorSlice";
import {
  Box,
  Divider,
  MenuItem,
  Stack,
  Typography,
  Alert,
  IconButton,
  Tooltip,
  Paper,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  InputAdornment,
} from "@mui/material";
import { CustomSelect } from "../../components/custom_select/CustomSelect";
import { CustomButton } from "../../components/custom_button/CustomButton";
import { CurrencySelect } from "../../components/currency_select/CurrencySelect";

import UploadIcon from "@mui/icons-material/Upload";
import DeleteIcon from "@mui/icons-material/Delete";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import AddIcon from "@mui/icons-material/Add";
import { HomeCard } from "../../components/home_card/HomeCard";
import QrCodeGeneratorImage from "../../assets/QrCodeGenerator.webp";
import BarcodeGeneratorImage from "../../assets/BarcodeGenerator.webp";
import { useState, useCallback, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import AdSense from "../../components/adsense/AdSense";
import { ADSENSE_SLOTS } from "../../config/adsense";

export function InvoiceGenerator() {
  const dispatch = useAppDispatch();
  const state = useAppSelector(selectState);
  const logoInputRef = useRef<HTMLInputElement>(null);
  const { t } = useTranslation();

  const handleLogoSelect = useCallback(
    (files: FileList | null) => {
      if (!files || files.length === 0) return;

      const file = files[0];
      const validationError = validateLogoFile(file);
      if (validationError) {
        dispatch(clearError());
        console.error("Logo validation error:", validationError);
        return;
      }

      const reader = new FileReader();
      reader.addEventListener("load", () => {
        dispatch(logoChanged(reader.result as string));
      });
      reader.readAsDataURL(file);
    },
    [dispatch]
  );

  const handleAddItem = useCallback(() => {
    const newItem: InvoiceItem = {
      id: generateUniqueId(),
      description: "",
      quantity: 1,
      rate: 0,
      amount: 0,
    };
    dispatch(itemAdded(newItem));
  }, [dispatch]);

  const handleItemChange = useCallback(
    (id: string, field: keyof InvoiceItem, value: string | number) => {
      dispatch(itemUpdated({ id, updates: { [field]: value } }));
    },
    [dispatch]
  );

  const handleRemoveItem = useCallback(
    (id: string) => {
      dispatch(itemRemoved(id));
    },
    [dispatch]
  );

  const handleGeneratePdf = useCallback(() => {
    dispatch(generateInvoicePdfAsync());
  }, [dispatch]);

  const handleReset = useCallback(() => {
    dispatch(resetGenerator());
  }, [dispatch]);

  const currencyDisplayTypes = [
    { value: "symbol", label: t("invoiceGenerator.currencyDisplay.symbol") },
    { value: "iso", label: t("invoiceGenerator.currencyDisplay.iso") },
  ];

  return (
    <SideMenuPageContainer
      pageTitle={t("invoiceGenerator.title")}
      pageSeoTitle={t("invoiceGenerator.seoTitle")}
      pageDescription={t("invoiceGenerator.description")}
    >
      <Box sx={{ width: { xs: 300, sm: 350, md: 900, lg: 1200 } }}>
        <Grid2
          container
          spacing={2}
          sx={{ mt: 2 }}
          columns={{ xs: 6, sm: 6, md: 12, lg: 12 }}
        >
          <Grid2 xs={12}>
            <Typography variant="h2" sx={{ mb: 2 }}>
              {t("invoiceGenerator.mainHeading")}
            </Typography>
          </Grid2>

          {/* Logo Upload */}
          <Grid2 xs={12}>
            <Paper
              sx={{
                p: 3,
                mb: 2,
                background: "#2e303200",
                borderColor: "#282B39",
                borderStyle: "dashed",
              }}
            >
              <Typography variant="h4" sx={{ mb: 3 }}>
                {t("invoiceGenerator.companyLogo")}
              </Typography>

              <Box
                sx={{
                  border: "2px dashed #7895a1",
                  borderRadius: 2,
                  p: 4,
                  textAlign: "center",
                  cursor: "pointer",
                  mb: 2,
                }}
                onClick={() => logoInputRef.current?.click()}
              >
                {state.logoDataUrl ? (
                  <img
                    src={state.logoDataUrl}
                    alt="Company Logo"
                    style={{
                      maxWidth: "200px",
                      maxHeight: "100px",
                      objectFit: "contain",
                    }}
                  />
                ) : (
                  <>
                    <UploadIcon
                      sx={{ fontSize: 48, color: "#7895a1", mb: 2 }}
                    />
                    <Typography variant="h6" color={"secondary"} sx={{ mb: 1 }}>
                      {t("invoiceGenerator.clickToUploadLogo")}
                    </Typography>
                    <Typography variant="body2" color="info.main">
                      {t("invoiceGenerator.supportedLogoFormats")}
                    </Typography>
                  </>
                )}
              </Box>

              <input
                ref={logoInputRef}
                type="file"
                accept="image/*"
                onChange={(e) => handleLogoSelect(e.target.files)}
                style={{ display: "none" }}
              />

              {state.logoDataUrl && (
                <Button
                  variant="outlined"
                  color="error"
                  onClick={() => dispatch(logoChanged(null))}
                  sx={{ mt: 1 }}
                >
                  {t("invoiceGenerator.removeLogo")}
                </Button>
              )}
            </Paper>
          </Grid2>

          {/* Invoice Details */}
          <Grid2 xs={12}>
            <Paper
              sx={{
                p: 3,
                mb: 2,
                background: "#2e303200",
                borderColor: "#282B39",
                borderStyle: "dashed",
              }}
            >
              <Typography variant="h4" sx={{ mb: 3 }}>
                {t("invoiceGenerator.invoiceDetails")}
              </Typography>

              <Grid2 container spacing={2}>
                <Grid2 xs={12} md={6}>
                  <TextField
                    label={t("invoiceGenerator.invoiceNumber")}
                    value={state.invoiceNumber}
                    onChange={(e) =>
                      dispatch(invoiceNumberChanged(e.target.value))
                    }
                    placeholder={t("invoiceGenerator.invoiceNumberPlaceholder")}
                    fullWidth
                  />
                </Grid2>
                <Grid2 xs={12} md={6}>
                  <TextField
                    label={t("invoiceGenerator.date")}
                    type="date"
                    InputLabelProps={{ shrink: true }}
                    value={state.date}
                    onChange={(e) => dispatch(dateChanged(e.target.value))}
                    fullWidth
                  />
                </Grid2>
                <Grid2 xs={12} md={6}>
                  <CurrencySelect
                    label={t("invoiceGenerator.currency")}
                    value={state.currency}
                    onChange={(e) => dispatch(currencyChanged(e.target.value))}
                  />
                </Grid2>
                <Grid2 xs={12} md={6}>
                  <CustomSelect
                    label={t("invoiceGenerator.currencyDisplayType")}
                    value={state.currencyDisplayType}
                    onChange={(e) =>
                      dispatch(
                        currencyDisplayTypeChanged(
                          e.target.value as "symbol" | "iso"
                        )
                      )
                    }
                  >
                    {currencyDisplayTypes.map((type) => (
                      <MenuItem key={type.value} value={type.value}>
                        {type.label}
                      </MenuItem>
                    ))}
                  </CustomSelect>
                </Grid2>
                <Grid2 xs={12} md={6}>
                  <TextField
                    label={t("invoiceGenerator.dueDateOptional")}
                    variant="outlined"
                    type="date"
                    InputLabelProps={{ shrink: true }}
                    value={state.dueDate}
                    onChange={(e) => dispatch(dueDateChanged(e.target.value))}
                    fullWidth
                  />
                </Grid2>
                <Grid2 xs={12} md={6}>
                  <TextField
                    label={t("invoiceGenerator.poNumberOptional")}
                    value={state.poNumber}
                    onChange={(e) => dispatch(poNumberChanged(e.target.value))}
                    placeholder={t("invoiceGenerator.poNumberPlaceholder")}
                    fullWidth
                  />
                </Grid2>
                <Grid2 xs={12} md={6}>
                  <TextField
                    label={t("invoiceGenerator.paymentTermsOptional")}
                    value={state.paymentTerms}
                    onChange={(e) =>
                      dispatch(paymentTermsChanged(e.target.value))
                    }
                    placeholder={t("invoiceGenerator.paymentTermsPlaceholder")}
                    fullWidth
                  />
                </Grid2>
              </Grid2>
            </Paper>
          </Grid2>

          {/* From Information */}
          <Grid2 xs={12} md={6}>
            <Paper
              sx={{
                p: 3,
                mb: 2,
                background: "#2e303200",
                borderColor: "#282B39",
                borderStyle: "dashed",
              }}
            >
              <Typography variant="h4" sx={{ mb: 3 }}>
                {t("invoiceGenerator.fromSectionTitle")}
              </Typography>

              <Stack spacing={2}>
                <TextField
                  label={t("invoiceGenerator.companyNameRequired")}
                  value={state.fromName}
                  onChange={(e) => dispatch(fromNameChanged(e.target.value))}
                  placeholder={t("invoiceGenerator.companyNamePlaceholder")}
                  fullWidth
                />
                <TextField
                  label={t("invoiceGenerator.address")}
                  value={state.fromAddress}
                  onChange={(e) => dispatch(fromAddressChanged(e.target.value))}
                  placeholder={t("invoiceGenerator.addressPlaceholder")}
                  fullWidth
                />
                <Grid2 container spacing={1}>
                  <Grid2 xs={4}>
                    <TextField
                      label={t("invoiceGenerator.city")}
                      value={state.fromCity}
                      onChange={(e) =>
                        dispatch(fromCityChanged(e.target.value))
                      }
                      placeholder={t("invoiceGenerator.city")}
                      fullWidth
                    />
                  </Grid2>
                  <Grid2 xs={4}>
                    <TextField
                      label={t("invoiceGenerator.state")}
                      value={state.fromState}
                      onChange={(e) =>
                        dispatch(fromStateChanged(e.target.value))
                      }
                      placeholder={t("invoiceGenerator.state")}
                      fullWidth
                    />
                  </Grid2>
                  <Grid2 xs={4}>
                    <TextField
                      label={t("invoiceGenerator.zip")}
                      value={state.fromZip}
                      onChange={(e) => dispatch(fromZipChanged(e.target.value))}
                      placeholder={t("invoiceGenerator.zipPlaceholder")}
                      fullWidth
                    />
                  </Grid2>
                </Grid2>
                <TextField
                  label={t("invoiceGenerator.country")}
                  value={state.fromCountry}
                  onChange={(e) => dispatch(fromCountryChanged(e.target.value))}
                  placeholder={t("invoiceGenerator.country")}
                  fullWidth
                />
                <TextField
                  label={t("invoiceGenerator.email")}
                  type="email"
                  value={state.fromEmail}
                  onChange={(e) => dispatch(fromEmailChanged(e.target.value))}
                  placeholder={t("invoiceGenerator.emailPlaceholder")}
                  fullWidth
                />
                <TextField
                  label={t("invoiceGenerator.phone")}
                  value={state.fromPhone}
                  onChange={(e) => dispatch(fromPhoneChanged(e.target.value))}
                  placeholder={t("invoiceGenerator.phonePlaceholder")}
                  fullWidth
                />
              </Stack>
            </Paper>
          </Grid2>

          {/* Bill To Information */}
          <Grid2 xs={12} md={6}>
            <Paper
              sx={{
                p: 3,
                mb: 2,
                background: "#2e303200",
                borderColor: "#282B39",
                borderStyle: "dashed",
              }}
            >
              <Typography variant="h4" sx={{ mb: 3 }}>
                {t("invoiceGenerator.billToSectionTitle")}
              </Typography>

              <Stack spacing={2}>
                <TextField
                  label={t("invoiceGenerator.clientName")}
                  value={state.billToName}
                  onChange={(e) => dispatch(billToNameChanged(e.target.value))}
                  placeholder={t("invoiceGenerator.clientCompanyPlaceholder")}
                  fullWidth
                />
                <TextField
                  label={t("invoiceGenerator.address")}
                  value={state.billToAddress}
                  onChange={(e) =>
                    dispatch(billToAddressChanged(e.target.value))
                  }
                  placeholder={t("invoiceGenerator.addressPlaceholder")}
                  fullWidth
                />
                <Grid2 container spacing={1}>
                  <Grid2 xs={4}>
                    <TextField
                      label={t("invoiceGenerator.city")}
                      value={state.billToCity}
                      onChange={(e) =>
                        dispatch(billToCityChanged(e.target.value))
                      }
                      placeholder={t("invoiceGenerator.city")}
                      fullWidth
                    />
                  </Grid2>
                  <Grid2 xs={4}>
                    <TextField
                      label={t("invoiceGenerator.state")}
                      value={state.billToState}
                      onChange={(e) =>
                        dispatch(billToStateChanged(e.target.value))
                      }
                      placeholder={t("invoiceGenerator.state")}
                      fullWidth
                    />
                  </Grid2>
                  <Grid2 xs={4}>
                    <TextField
                      label={t("invoiceGenerator.zip")}
                      value={state.billToZip}
                      onChange={(e) =>
                        dispatch(billToZipChanged(e.target.value))
                      }
                      placeholder={t("invoiceGenerator.zipPlaceholder")}
                      fullWidth
                    />
                  </Grid2>
                </Grid2>
                <TextField
                  label={t("invoiceGenerator.country")}
                  value={state.billToCountry}
                  onChange={(e) =>
                    dispatch(billToCountryChanged(e.target.value))
                  }
                  placeholder={t("invoiceGenerator.country")}
                  fullWidth
                />
                <TextField
                  label={t("invoiceGenerator.email")}
                  type="email"
                  value={state.billToEmail}
                  onChange={(e) => dispatch(billToEmailChanged(e.target.value))}
                  placeholder={t("invoiceGenerator.clientEmailPlaceholder")}
                  fullWidth
                />
                <TextField
                  label={t("invoiceGenerator.phone")}
                  value={state.billToPhone}
                  onChange={(e) => dispatch(billToPhoneChanged(e.target.value))}
                  placeholder={t("invoiceGenerator.phonePlaceholder")}
                  fullWidth
                />
              </Stack>
            </Paper>
          </Grid2>

          {/* Ship To Information */}
          <Grid2 xs={12}>
            <Paper
              sx={{
                p: 3,
                mb: 2,
                background: "#2e303200",
                borderColor: "#282B39",
                borderStyle: "dashed",
              }}
            >
              <Typography variant="h4" sx={{ mb: 3 }}>
                {t("invoiceGenerator.shipToSectionTitle")}
              </Typography>

              <Grid2 container spacing={2}>
                <Grid2 xs={12} md={6}>
                  <TextField
                    label={t("invoiceGenerator.shipToName")}
                    value={state.shipToName}
                    onChange={(e) =>
                      dispatch(shipToNameChanged(e.target.value))
                    }
                    placeholder={t("invoiceGenerator.shipToNamePlaceholder")}
                    fullWidth
                  />
                </Grid2>
                <Grid2 xs={12} md={6}>
                  <TextField
                    label={t("invoiceGenerator.shipToAddress")}
                    value={state.shipToAddress}
                    onChange={(e) =>
                      dispatch(shipToAddressChanged(e.target.value))
                    }
                    placeholder={t("invoiceGenerator.shipToAddressPlaceholder")}
                    fullWidth
                  />
                </Grid2>
                <Grid2 xs={12} md={4}>
                  <TextField
                    label={t("invoiceGenerator.shipToCity")}
                    value={state.shipToCity}
                    onChange={(e) =>
                      dispatch(shipToCityChanged(e.target.value))
                    }
                    placeholder={t("invoiceGenerator.city")}
                    fullWidth
                  />
                </Grid2>
                <Grid2 xs={12} md={4}>
                  <TextField
                    label={t("invoiceGenerator.shipToState")}
                    value={state.shipToState}
                    onChange={(e) =>
                      dispatch(shipToStateChanged(e.target.value))
                    }
                    placeholder={t("invoiceGenerator.state")}
                    fullWidth
                  />
                </Grid2>
                <Grid2 xs={12} md={4}>
                  <TextField
                    label={t("invoiceGenerator.shipToZip")}
                    value={state.shipToZip}
                    onChange={(e) => dispatch(shipToZipChanged(e.target.value))}
                    placeholder={t("invoiceGenerator.zipPlaceholder")}
                    fullWidth
                  />
                </Grid2>
                <Grid2 xs={12}>
                  <TextField
                    label={t("invoiceGenerator.shipToCountry")}
                    value={state.shipToCountry}
                    onChange={(e) =>
                      dispatch(shipToCountryChanged(e.target.value))
                    }
                    placeholder={t("invoiceGenerator.country")}
                    fullWidth
                  />
                </Grid2>
              </Grid2>
            </Paper>
          </Grid2>

          {/* Items */}
          <Grid2 xs={12}>
            <Paper
              sx={{
                p: 3,
                mb: 2,
                background: "#2e303200",
                borderColor: "#282B39",
                borderStyle: "dashed",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 3,
                }}
              >
                <Typography variant="h4">
                  {t("invoiceGenerator.itemsSectionTitle")}
                </Typography>
                <CustomButton
                  icon={<AddIcon sx={{ color: "#FFD9A8" }} />}
                  label={t("invoiceGenerator.addItem")}
                  onClick={handleAddItem}
                />
              </Box>

              {state.items.length === 0 ? (
                <Box sx={{ textAlign: "center", py: 4 }}>
                  <Typography variant="body1" color="info.main">
                    {t("invoiceGenerator.noItemsMessage")}
                  </Typography>
                </Box>
              ) : (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>
                          <Typography>
                            {t("invoiceGenerator.headers.description")}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography>
                            {t("invoiceGenerator.headers.quantity")}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography>
                            {t("invoiceGenerator.headers.rate")}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography>
                            {t("invoiceGenerator.headers.amount")}
                          </Typography>
                        </TableCell>
                        <TableCell align="center"></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {state.items.map((item) => (
                        <TableRow key={item.id}>
                          <TableCell>
                            <TextField
                              fullWidth
                              size="small"
                              value={item.description}
                              onChange={(e) =>
                                handleItemChange(
                                  item.id,
                                  "description",
                                  e.target.value
                                )
                              }
                              placeholder={t(
                                "invoiceGenerator.itemDescriptionPlaceholder"
                              )}
                              sx={{
                                "& .MuiOutlinedInput-root": {
                                  backgroundColor: "#2e3032",
                                  "& fieldset": {
                                    borderColor: "#7895a1",
                                  },
                                  "&:hover fieldset": {
                                    borderColor: "#FFD9A8",
                                  },
                                },
                                "& .MuiInputBase-input": {
                                  color: "#FFFFFF",
                                },
                              }}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <TextField
                              size="small"
                              type="number"
                              value={item.quantity}
                              onChange={(e) =>
                                handleItemChange(
                                  item.id,
                                  "quantity",
                                  parseFloat(e.target.value) || 0
                                )
                              }
                              sx={{
                                width: "80px",
                                "& .MuiOutlinedInput-root": {
                                  backgroundColor: "#2e3032",
                                  "& fieldset": {
                                    borderColor: "#7895a1",
                                  },
                                  "&:hover fieldset": {
                                    borderColor: "#FFD9A8",
                                  },
                                },
                                "& .MuiInputBase-input": {
                                  color: "#FFFFFF",
                                },
                              }}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <TextField
                              size="small"
                              type="number"
                              value={item.rate}
                              onChange={(e) =>
                                handleItemChange(
                                  item.id,
                                  "rate",
                                  parseFloat(e.target.value) || 0
                                )
                              }
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <Typography>{state.currency}</Typography>
                                  </InputAdornment>
                                ),
                              }}
                              sx={{
                                width: "120px",
                                "& .MuiOutlinedInput-root": {
                                  backgroundColor: "#2e3032",
                                  "& fieldset": {
                                    borderColor: "#7895a1",
                                  },
                                  "&:hover fieldset": {
                                    borderColor: "#FFD9A8",
                                  },
                                },
                                "& .MuiInputBase-input": {
                                  color: "#FFFFFF",
                                },
                                "& .MuiInputAdornment-root": {
                                  color: "#7895a1",
                                },
                              }}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Typography
                              variant="body2"
                              sx={{ color: "#FFD9A8", fontWeight: "bold" }}
                            >
                              {getCurrencyDisplay(
                                state.currency,
                                state.currencyDisplayType
                              )}{" "}
                              {item.amount.toFixed(2)}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Tooltip title={t("invoiceGenerator.removeItem")}>
                              <IconButton
                                onClick={() => handleRemoveItem(item.id)}
                                color="secondary"
                                size="small"
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Paper>
          </Grid2>

          {/* Totals */}
          <Grid2 xs={12} md={6}>
            <Paper
              sx={{
                p: 3,
                mb: 2,
                background: "#2e303200",
                borderColor: "#282B39",
                borderStyle: "dashed",
              }}
            >
              <Typography variant="h4" sx={{ mb: 3 }}>
                {t("invoiceGenerator.totalsSectionTitle")}
              </Typography>

              <Stack spacing={2}>
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography>{t("invoiceGenerator.subtotal")}</Typography>
                  <Typography sx={{ color: "#FFD9A8", fontWeight: "bold" }}>
                    {getCurrencyDisplay(
                      state.currency,
                      state.currencyDisplayType
                    )}{" "}
                    {state.subtotal.toFixed(2)}
                  </Typography>
                </Box>

                <TextField
                  label={t("invoiceGenerator.taxPercentage")}
                  type="number"
                  variant="outlined"
                  value={state.taxPercentage}
                  color={"info"}
                  onChange={(e) =>
                    dispatch(
                      taxPercentageChanged(parseFloat(e.target.value) || 0)
                    )
                  }
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Typography>%</Typography>
                      </InputAdornment>
                    ),
                  }}
                  fullWidth
                />

                {state.taxPercentage > 0 && (
                  <Box
                    sx={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <Typography>{t("invoiceGenerator.taxAmount")}</Typography>
                    <Typography sx={{ color: "#FFD9A8", fontWeight: "bold" }}>
                      {getCurrencyDisplay(
                        state.currency,
                        state.currencyDisplayType
                      )}{" "}
                      {state.taxAmount.toFixed(2)}
                    </Typography>
                  </Box>
                )}

                <TextField
                  label={t("invoiceGenerator.discountPercentage")}
                  type="number"
                  value={state.discountPercentage}
                  onChange={(e) =>
                    dispatch(
                      discountPercentageChanged(parseFloat(e.target.value) || 0)
                    )
                  }
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Typography>%</Typography>
                      </InputAdornment>
                    ),
                  }}
                  fullWidth
                />

                {state.discountPercentage > 0 && (
                  <Box
                    sx={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <Typography>
                      {t("invoiceGenerator.discountAmount")}
                    </Typography>
                    <Typography sx={{ color: "#FFD9A8", fontWeight: "bold" }}>
                      {getCurrencyDisplay(
                        state.currency,
                        state.currencyDisplayType
                      )}{" "}
                      {state.discountAmount.toFixed(2)}
                    </Typography>
                  </Box>
                )}

                <TextField
                  label={t("invoiceGenerator.shipping")}
                  type="number"
                  value={state.shipping}
                  onChange={(e) =>
                    dispatch(shippingChanged(parseFloat(e.target.value) || 0))
                  }
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Typography>
                          {getCurrencyDisplay(
                            state.currency,
                            state.currencyDisplayType
                          )}
                        </Typography>
                      </InputAdornment>
                    ),
                  }}
                  fullWidth
                />

                <Divider sx={{ backgroundColor: "#7895a1" }} />

                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography variant="h6" color={"info.main"}>
                    {t("invoiceGenerator.total")}
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{ color: "#FFD9A8", fontWeight: "bold" }}
                  >
                    {getCurrencyDisplay(
                      state.currency,
                      state.currencyDisplayType
                    )}{" "}
                    {state.total.toFixed(2)}
                  </Typography>
                </Box>

                <TextField
                  label={t("invoiceGenerator.amountPaid")}
                  type="number"
                  value={state.amountPaid}
                  onChange={(e) =>
                    dispatch(amountPaidChanged(parseFloat(e.target.value) || 0))
                  }
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Typography>
                          {getCurrencyDisplay(
                            state.currency,
                            state.currencyDisplayType
                          )}
                        </Typography>
                      </InputAdornment>
                    ),
                  }}
                  fullWidth
                />

                {state.amountPaid > 0 && (
                  <Box
                    sx={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <Typography variant="h6" color={"info.main"}>
                      {t("invoiceGenerator.balanceDue")}
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{ color: "#FFD9A8", fontWeight: "bold" }}
                    >
                      {getCurrencyDisplay(
                        state.currency,
                        state.currencyDisplayType
                      )}{" "}
                      {state.balanceDue.toFixed(2)}
                    </Typography>
                  </Box>
                )}
              </Stack>
            </Paper>
          </Grid2>

          {/* Notes and Terms */}
          <Grid2 xs={12} md={6}>
            <Paper
              sx={{
                p: 3,
                mb: 2,
                background: "#2e303200",
                borderColor: "#282B39",
                borderStyle: "dashed",
              }}
            >
              <Typography variant="h4" sx={{ mb: 3 }}>
                {t("invoiceGenerator.additionalSectionTitle")}
              </Typography>

              <Stack spacing={2}>
                <TextField
                  label={t("invoiceGenerator.notesOptional")}
                  multiline
                  rows={4}
                  value={state.notes}
                  onChange={(e) => dispatch(notesChanged(e.target.value))}
                  placeholder={t("invoiceGenerator.notesPlaceholder")}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "#2e3032",
                      "& fieldset": {
                        borderColor: "#7895a1",
                      },
                      "&:hover fieldset": {
                        borderColor: "#FFD9A8",
                      },
                    },
                    "& .MuiInputBase-input": {
                      color: "#FFFFFF",
                    },
                    "& .MuiInputLabel-root": {
                      color: "#7895a1",
                    },
                  }}
                />

                <TextField
                  label={t("invoiceGenerator.termsOptional")}
                  multiline
                  rows={4}
                  value={state.terms}
                  onChange={(e) => dispatch(termsChanged(e.target.value))}
                  placeholder={t("invoiceGenerator.termsPlaceholder")}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "#2e3032",
                      "& fieldset": {
                        borderColor: "#7895a1",
                      },
                      "&:hover fieldset": {
                        borderColor: "#FFD9A8",
                      },
                    },
                    "& .MuiInputBase-input": {
                      color: "#FFFFFF",
                    },
                    "& .MuiInputLabel-root": {
                      color: "#7895a1",
                    },
                  }}
                />
              </Stack>
            </Paper>
          </Grid2>

          {/* Action Buttons */}
          <Grid2 xs={12}>
            <Stack direction="row" spacing={2}>
              <CustomButton
                icon={<UploadIcon sx={{ color: "#FFD9A8" }} />}
                label={
                  state.isGenerating
                    ? t("invoiceGenerator.actions.generating")
                    : t("invoiceGenerator.actions.generatePdf")
                }
                onClick={handleGeneratePdf}
                disabled={state.isGenerating || state.items.length === 0}
              />

              <Tooltip title={t("invoiceGenerator.actions.reset")}>
                <IconButton onClick={handleReset} color="secondary">
                  <RestartAltIcon />
                </IconButton>
              </Tooltip>
            </Stack>
          </Grid2>

          {/* Error Display */}
          {state.error && (
            <Grid2 xs={12}>
              <Alert
                severity="error"
                onClose={() => dispatch(clearError())}
                sx={{ mb: 2 }}
              >
                {state.error}
              </Alert>
            </Grid2>
          )}

          <Grid2 xs={12} sx={{ mt: 3, mb: 3 }}>
            <Divider sx={{ backgroundColor: "#7895a1" }}></Divider>
          </Grid2>

          {/* SEO Content */}
          <Grid2 xs={12}>
            <AdSense
                adSlot={ADSENSE_SLOTS.IN_ARTICLE}
                adFormat="fluid"
                adLayoutKey="-fb+5w+4e-db+86"
                sx={{ mb: 3 }}
              />
            <Typography variant="h3" sx={{ mb: 2 }}>
              {t("invoiceGenerator.content.freeHeading")}
            </Typography>
            <Typography variant="body1" sx={{ mb: 3 }}>
              {t("invoiceGenerator.content.freeDescription")}
            </Typography>

            <Typography variant="h3" sx={{ mb: 2 }}>
              {t("invoiceGenerator.content.professionalHeading")}
            </Typography>
            <Typography variant="body1" sx={{ mb: 3 }}>
              {t("invoiceGenerator.content.professionalDescription")}
            </Typography>

            <Typography variant="h3" sx={{ mb: 2 }}>
              {t("invoiceGenerator.content.easyHeading")}
            </Typography>
            <Typography variant="body1" sx={{ mb: 3 }}>
              {t("invoiceGenerator.content.easyDescription")}
            </Typography>
          </Grid2>

          <Grid2 xs={12}>
            <Typography variant="h3" sx={{ mt: 3, mb: 1 }}>
              {t("invoiceGenerator.related")}
            </Typography>
          </Grid2>

          <Grid2 xs={6}>
            <HomeCard
              languageResource="qrCodeGenerator"
              link="/QrCodeGenerator"
              smallCard={true}
              image={QrCodeGeneratorImage}
            />
          </Grid2>
          <Grid2 xs={6}>
            <HomeCard
              languageResource="barcodeGenerator"
              link="/BarcodeGenerator"
              smallCard={true}
              image={BarcodeGeneratorImage}
            />
          </Grid2>
        </Grid2>
      </Box>
    </SideMenuPageContainer>
  );
}
